import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Input, SpecraFormField, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { SharedCompanyPicker, useSharedRecordSearch } from '../../../components/SharedRecord';
import { GovernmentOrgPicker } from '../../Delivery/_shared/components/GovernmentOrgPicker';
import { useTenantId } from '../../Delivery/_shared/hooks/useTenantId';
import { capabilityHooks } from '../../Delivery/Registries/hooks';
import { pastPerformanceClient } from '../../Library/ppClient';
const TYPES = new Set([
    'money',
    'timing_range',
    'staffing',
    'bid_effort',
    'strategic_fit',
    'customer_procurement',
    'capability',
    'government_org',
    'vehicle',
    'company',
    'past_performance',
    'competition_bid'
]);
export function qualificationCriterionTypes(criteriaJson) {
    try {
        const criteria = JSON.parse(criteriaJson || '[]');
        if (!Array.isArray(criteria)) return {};
        return Object.fromEntries(criteria.filter((c)=>typeof c?.id === 'string' && TYPES.has(c.type)).map((c)=>[
                c.id,
                c.type
            ]));
    } catch  {
        return {};
    }
}
function assessment(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}
const text = (value)=>typeof value === 'string' ? value : '';
/** Template-specific assessments remain incomplete until the evaluator saves.
 * Canonical selection is evidence, never disposition or final eligibility. */ export function QualificationCriterion({ id, label, type, value, onChange }) {
    const a = assessment(value);
    const reference = assessment(a.reference);
    const resource = [
        'money',
        'timing_range',
        'staffing',
        'bid_effort'
    ].includes(type);
    const [choosing, setChoosing] = useState(false);
    const update = (key, value)=>onChange({
            state: 'unknown',
            ...a,
            [key]: value
        });
    const input = (key, caption)=>/*#__PURE__*/ _jsx(SpecraFormField, {
            label: caption,
            children: /*#__PURE__*/ _jsx(Input, {
                "aria-label": `${label} — ${caption.toLowerCase()}`,
                value: text(a[key]),
                onChange: (event)=>update(key, event.target.value),
                "data-testid": Testids.qualification.answerInput,
                "data-criterion-id": id,
                "data-field": key
            })
        }, key);
    const canonical = [
        'capability',
        'government_org',
        'vehicle',
        'company',
        'past_performance'
    ].includes(type);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2 rounded border p-3",
        children: [
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Answer state",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "aria-label": `${label} — answer state`,
                    value: text(a.state) || 'unknown',
                    onChange: (e)=>onChange({
                            ...a,
                            state: e.target.value,
                            ...e.target.value === 'unknown' ? {
                                signal: undefined
                            } : {},
                            ...resource ? {
                                knowledge: e.target.value === 'unknown' ? 'unknown' : 'manual_assessment'
                            } : {},
                            ...e.target.value === 'unknown' && resource && a.estimate != null ? {
                                estimate: {
                                    ...assessment(a.estimate),
                                    lower: null,
                                    upper: null,
                                    earliest: '',
                                    latest: ''
                                }
                            } : {}
                        }),
                    "data-testid": Testids.qualification.answerInput,
                    "data-criterion-id": id,
                    "data-field": "state",
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "unknown",
                            children: "Unknown — open question"
                        }),
                        /*#__PURE__*/ _jsx("option", {
                            value: "assessed",
                            children: "Assessed"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Evaluator signal",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "aria-label": `${label} — evaluator signal`,
                    value: text(a.signal),
                    disabled: text(a.state) !== 'assessed',
                    onChange: (e)=>onChange({
                            ...a,
                            signal: e.target.value
                        }),
                    "data-testid": Testids.qualification.answerInput,
                    "data-criterion-id": id,
                    "data-field": "signal",
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "No signal"
                        }),
                        /*#__PURE__*/ _jsx("option", {
                            value: "strength",
                            children: "Strength"
                        }),
                        /*#__PURE__*/ _jsx("option", {
                            value: "neutral",
                            children: "Neutral"
                        }),
                        /*#__PURE__*/ _jsx("option", {
                            value: "concern",
                            children: "Concern"
                        })
                    ]
                })
            }),
            value != null && typeof value !== 'object' && /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Source answer: ",
                    String(value),
                    ". Editing records a separate manual assessment."
                ]
            }),
            resource && /*#__PURE__*/ _jsx(QualificationEstimate, {
                id: id,
                label: label,
                type: type,
                value: a,
                onChange: onChange
            }),
            canonical && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    reference.id && /*#__PURE__*/ _jsxs("p", {
                        children: [
                            text(reference.label),
                            " · ",
                            text(reference.id),
                            " (recorded selection)"
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        type: "button",
                        size: "sm",
                        variant: "outline",
                        onClick: ()=>setChoosing(!choosing),
                        children: choosing ? 'Close picker' : `Choose ${type.replace('_', ' ')}`
                    }),
                    reference.id && /*#__PURE__*/ _jsx(Button, {
                        type: "button",
                        size: "sm",
                        variant: "outline",
                        onClick: ()=>update('reference', null),
                        children: "Clear reference"
                    }),
                    choosing && /*#__PURE__*/ _jsx(QualificationReferencePicker, {
                        type: type,
                        id: id,
                        value: text(reference.id),
                        onSelect: (id, label)=>{
                            update('reference', {
                                id,
                                label
                            });
                            setChoosing(false);
                        }
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-muted-foreground",
                        children: "A reference supports evaluation; it does not confirm access or solicitation eligibility. Recorded labels remain historical after retirement."
                    })
                ]
            }),
            type === 'strategic_fit' && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    input('fit', 'Qualitative fit'),
                    input('strategicRole', 'Intended strategic role')
                ]
            }),
            type === 'customer_procurement' && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    input('relationship', 'Relationship position'),
                    input('access', 'Access expectation'),
                    input('setAside', 'Set-aside expectation'),
                    input('acquisitionType', 'Acquisition type expectation'),
                    input('contractType', 'Contract type expectation'),
                    input('timing', 'Timing expectation')
                ]
            }),
            type === 'competition_bid' && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Intended bid position",
                        children: /*#__PURE__*/ _jsx(SpecraSelect, {
                            "aria-label": `${label} — intended bid position`,
                            value: text(a.position) || 'undecided',
                            onChange: (e)=>update('position', e.target.value),
                            children: [
                                'undecided',
                                'prime',
                                'sub',
                                'jv'
                            ].map((p)=>/*#__PURE__*/ _jsx("option", {
                                    value: p,
                                    children: p
                                }, p))
                        })
                    }),
                    input('incumbent', 'Incumbent assessment'),
                    input('competitors', 'Possible competitors'),
                    input('teamingNeeds', 'High-level teaming needs'),
                    /*#__PURE__*/ _jsx("p", {
                        children: "Hypotheses: bidder intent, partner availability and relationship advantages remain unverified. No agreement or partner selection is created."
                    })
                ]
            }),
            type !== 'competition_bid' && /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Assessment basis",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "aria-label": `${label} — assessment basis`,
                    value: text(a.knowledge) || 'manual_assessment',
                    onChange: (e)=>e.target.value === 'unknown' && resource ? onChange({
                            ...a,
                            state: 'unknown',
                            knowledge: 'unknown',
                            ...a.estimate != null ? {
                                estimate: {
                                    ...assessment(a.estimate),
                                    lower: null,
                                    upper: null,
                                    earliest: '',
                                    latest: ''
                                }
                            } : {}
                        }) : update('knowledge', e.target.value),
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "manual_assessment",
                            children: "Manual assessment"
                        }),
                        /*#__PURE__*/ _jsx("option", {
                            value: "hypothesis",
                            children: "Hypothesis / informal expectation"
                        }),
                        /*#__PURE__*/ _jsx("option", {
                            value: "unknown",
                            children: "Unknown"
                        })
                    ]
                })
            }),
            input('rationale', type === 'open_question' ? 'Answer assessment' : 'Rationale'),
            input('uncertainty', 'Remaining uncertainty'),
            input('evidenceNeeded', 'Evidence needed'),
            /*#__PURE__*/ _jsx(QualificationFollowUp, {
                id: id,
                label: label,
                value: a,
                onChange: onChange
            }),
            type === 'capability' && /*#__PURE__*/ _jsx("p", {
                children: "Missing outcomes or metrics: record a manual Delivery data-call note in Evidence needed."
            })
        ]
    });
}
const MONEY_MEASURES = [
    [
        'deal_value',
        'Deal value'
    ],
    [
        'contract_obligation',
        'Contract obligation'
    ],
    [
        'ceiling_options',
        'Ceiling / options'
    ],
    [
        'our_workshare',
        'Our subcontract / workshare'
    ],
    [
        'expected_company_share',
        'Expected company share'
    ],
    [
        'bid_price',
        'Bid price'
    ],
    [
        'eventual_revenue',
        'Eventual revenue'
    ]
];
const TIMING_MEASURES = [
    [
        'expected_timing',
        'Expected timing'
    ],
    [
        'current_completion',
        'Current completion'
    ],
    [
        'ultimate_completion',
        'Ultimate completion'
    ],
    [
        'potential_recompete',
        'Potential recompete horizon (derived)'
    ],
    [
        'confirmed_response',
        'Confirmed response date'
    ]
];
function QualificationEstimate({ id, label, type, value: a, onChange }) {
    const timing = type === 'timing_range';
    const defaults = {
        measure: type === 'money' ? 'deal_value' : timing ? 'expected_timing' : type === 'staffing' ? 'rough_staffing' : 'bid_effort',
        lower: null,
        upper: null,
        unit: type === 'money' ? 'USD' : timing ? 'date' : type === 'staffing' ? 'people' : 'hours'
    };
    const e = {
        ...defaults,
        ...assessment(a.estimate)
    };
    const update = (key, value)=>onChange({
            state: 'unknown',
            ...a,
            estimate: {
                ...e,
                [key]: value
            }
        });
    const field = (key, caption, inputType = 'text')=>/*#__PURE__*/ _jsx(SpecraFormField, {
            label: caption,
            children: /*#__PURE__*/ _jsx(Input, {
                "aria-label": `${label} — ${caption.toLowerCase()}`,
                type: inputType,
                value: typeof e[key] === 'number' ? String(e[key]) : text(assessment(e)[key]),
                disabled: a.state !== 'assessed' && [
                    'lower',
                    'upper',
                    'earliest',
                    'latest'
                ].includes(key),
                onChange: (event)=>update(key, inputType === 'number' ? event.target.value === '' ? null : Number(event.target.value) : event.target.value),
                "data-testid": Testids.qualification.answerInput,
                "data-criterion-id": id,
                "data-field": `estimate.${key}`
            })
        }, key);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        children: [
            /*#__PURE__*/ _jsx("p", {
                children: a.state !== 'assessed' ? type === 'money' ? 'Unknown amount' : 'Unknown estimate' : 'Estimated range'
            }),
            (type === 'money' || timing) && /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Measure",
                children: /*#__PURE__*/ _jsx(SpecraSelect, {
                    "aria-label": `${label} — measure`,
                    value: e.measure,
                    onChange: (event)=>update('measure', event.target.value),
                    "data-testid": Testids.qualification.answerInput,
                    "data-criterion-id": id,
                    "data-field": "estimate.measure",
                    children: (timing ? TIMING_MEASURES : MONEY_MEASURES).map(([key, caption])=>/*#__PURE__*/ _jsx("option", {
                            value: key,
                            children: caption
                        }, key))
                })
            }),
            timing ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    field('earliest', 'Earliest date', 'date'),
                    field('latest', 'Latest date', 'date')
                ]
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    field('lower', 'Lower estimate', 'number'),
                    field('upper', 'Upper estimate', 'number')
                ]
            }),
            field('basis', 'Estimate basis'),
            field('asOf', 'As of date', 'date'),
            field('scope', 'Scope'),
            field('period', 'Period'),
            field('unit', 'Unit'),
            /*#__PURE__*/ _jsx("p", {
                children: "Blank bounds mean Unknown. Compare only the same measure, scope, period and unit. No pricing or staffing model is required."
            }),
            timing && /*#__PURE__*/ _jsx("p", {
                children: "Completion dates and derived potential recompete horizons do not confirm response deadlines. Record confirmed response dates separately."
            })
        ]
    });
}
export function QualificationFollowUp({ id, label, value: a, onChange }) {
    const f = assessment(a.followUp);
    const [adding, setAdding] = useState(false);
    const update = (key, value)=>onChange({
            state: 'unknown',
            ...a,
            followUp: {
                status: 'open',
                ...f,
                [key]: value
            }
        });
    const field = (key, caption, type = 'text')=>/*#__PURE__*/ _jsx(SpecraFormField, {
            label: caption,
            children: /*#__PURE__*/ _jsx(Input, {
                "aria-label": `${label} — ${caption.toLowerCase()}`,
                type: type,
                value: text(f[key]),
                onChange: (event)=>update(key, event.target.value),
                "data-testid": Testids.qualification.answerInput,
                "data-criterion-id": id,
                "data-field": `followUp.${key}`
            })
        }, key);
    if (!a.followUp && !adding) return /*#__PURE__*/ _jsx(Button, {
        type: "button",
        size: "sm",
        variant: "outline",
        onClick: ()=>setAdding(true),
        "aria-label": `Add follow-up — ${label}`,
        children: "Add follow-up"
    });
    return /*#__PURE__*/ _jsxs("fieldset", {
        className: "space-y-2 rounded border p-2",
        children: [
            /*#__PURE__*/ _jsxs("legend", {
                children: [
                    "Follow-up — ",
                    label
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Track this question with an accountable person. Completing contact leaves the answer and its basis unchanged; assess the answer separately."
            }),
            field('owner', 'Accountable person'),
            field('dueDate', 'Follow-up due date', 'date'),
            field('notes', 'Follow-up notes'),
            field('sourcePerson', 'Source person (no account required)'),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Information received by",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "aria-label": `${label} — information received by`,
                    value: text(f.channel),
                    onChange: (event)=>update('channel', event.target.value),
                    "data-testid": Testids.qualification.answerInput,
                    "data-criterion-id": id,
                    "data-field": "followUp.channel",
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "Unknown"
                        }),
                        /*#__PURE__*/ _jsx("option", {
                            value: "call",
                            children: "Call"
                        }),
                        /*#__PURE__*/ _jsx("option", {
                            value: "email",
                            children: "Email"
                        }),
                        /*#__PURE__*/ _jsx("option", {
                            value: "manual",
                            children: "Manual entry"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Follow-up status",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "aria-label": `${label} — follow-up status`,
                    value: text(f.status) || 'open',
                    onChange: (event)=>update('status', event.target.value),
                    "data-testid": Testids.qualification.answerInput,
                    "data-criterion-id": id,
                    "data-field": "followUp.status",
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "open",
                            children: "Open"
                        }),
                        /*#__PURE__*/ _jsx("option", {
                            value: "completed",
                            children: "Completed"
                        })
                    ]
                })
            })
        ]
    });
}
function QualificationReferencePicker({ type, id, value, onSelect }) {
    if (type === 'company') return /*#__PURE__*/ _jsx(SharedCompanyPicker, {
        value: value,
        onSelect: onSelect
    });
    if (type === 'government_org') return /*#__PURE__*/ _jsx(GovernmentOrgPicker, {
        id: id,
        label: "Contracting / customer office",
        value: value,
        onChange: (id)=>onSelect(id, ''),
        testid: Testids.qualification.answerInput
    });
    if (type === 'past_performance') return /*#__PURE__*/ _jsx(QualificationPPPicker, {
        onSelect: onSelect
    });
    if (type === 'capability') return /*#__PURE__*/ _jsx(QualificationCapabilityPicker, {
        onSelect: onSelect
    });
    return /*#__PURE__*/ _jsx(QualificationVehiclePicker, {
        onSelect: onSelect
    });
}
function QualificationCapabilityPicker({ onSelect }) {
    const tenantId = useTenantId();
    const result = capabilityHooks.useList({
        companyId: tenantId
    });
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            result.isLoading && /*#__PURE__*/ _jsx("p", {
                children: "Loading capabilities…"
            }),
            result.isError && /*#__PURE__*/ _jsx("p", {
                children: "Capabilities unavailable. Continue with an unknown and an evidence request."
            }),
            /*#__PURE__*/ _jsxs(SpecraSelect, {
                "aria-label": "Canonical capability",
                defaultValue: "",
                onChange: (e)=>{
                    const c = result.data?.items.find((c)=>c.id === e.target.value);
                    if (c) onSelect(c.id, c.name);
                },
                children: [
                    /*#__PURE__*/ _jsx("option", {
                        value: "",
                        children: "Choose an active capability"
                    }),
                    (result.data?.items ?? []).map((c)=>/*#__PURE__*/ _jsx("option", {
                            value: c.id,
                            children: c.name
                        }, c.id))
                ]
            })
        ]
    });
}
function QualificationVehiclePicker({ onSelect }) {
    const [term, setTerm] = useState('');
    const result = useSharedRecordSearch(term);
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(Input, {
                "aria-label": "Search canonical vehicles",
                value: term,
                onChange: (e)=>setTerm(e.target.value)
            }),
            result.isFetching && /*#__PURE__*/ _jsx("p", {
                children: "Searching vehicles…"
            }),
            result.data?.unavailable.includes('vehicle') && /*#__PURE__*/ _jsx("p", {
                children: "Vehicles unavailable. Continue with an unknown and an evidence request."
            }),
            result.data?.hits.filter((hit)=>hit.kind === 'vehicle').map((hit)=>/*#__PURE__*/ _jsxs(Button, {
                    type: "button",
                    variant: "outline",
                    onClick: ()=>onSelect(hit.id, hit.title),
                    children: [
                        hit.title,
                        " · ",
                        hit.context
                    ]
                }, hit.id))
        ]
    });
}
function QualificationPPPicker({ onSelect }) {
    const [term, setTerm] = useState('');
    const result = useQuery({
        queryKey: [
            'qualification',
            'candidate-pp'
        ],
        queryFn: async ()=>(await pastPerformanceClient.listPastPerformanceReferences({
                offset: 0,
                limit: 200
            })).references
    });
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(Input, {
                "aria-label": "Search candidate PP references",
                value: term,
                onChange: (e)=>setTerm(e.target.value)
            }),
            result.isFetching && /*#__PURE__*/ _jsx("p", {
                children: "Searching permitted Library references…"
            }),
            result.isError && /*#__PURE__*/ _jsx("p", {
                children: "References unavailable. Record the evidence needed and continue manually."
            }),
            result.data?.filter((reference)=>reference.item?.title.toLowerCase().includes(term.toLowerCase())).map((reference)=>reference.item).filter((item)=>!!item).map((item)=>/*#__PURE__*/ _jsx(Button, {
                    type: "button",
                    variant: "outline",
                    onClick: ()=>onSelect(item.id, item.title),
                    children: item.title
                }, item.id)),
            /*#__PURE__*/ _jsx("p", {
                children: "Partner and historical references do not require an internal Program. Candidate selection is not final eligibility. Open permitted evidence in the Library."
            })
        ]
    });
}
